import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  ALL_GROUP_KEY,
  Box,
  BreadcrumbItem,
  BreadcrumbPage,
  Button,
  createColumnHelper,
  DataTable,
  DataTableCellContent,
  EmptyState,
  Icon,
  MenuItem,
  MenuSeparator,
  Text,
  type BulkAction,
  type DataTableColumnDef,
  type DataTableGroupType,
  type SortingState,
} from "@salestrak/ui";
import { ViewSearch } from "#app/components/filters/view-search";
import { PageHeader, PageHeaderActions, PageHeaderBreadcrumb } from "#app/components/page-header";

import {
  TRASH_RETENTION_DAYS,
  trashDaysRemaining,
  trashPurgeAt,
  useTrashList,
  useTrashMutations,
} from "#app/hooks/settings/use-trash-mutations";
import { DateText } from "#app/components/date-text";
import { type TrashRow } from "#app/lib/data";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { EntityTag } from "#app/components/entity";
import {
  LocalGroupProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import { settleAll } from "#app/lib/utils/settle-all";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission } from "#app/lib/hooks/use-permission";
import { PageLoading } from "#app/components/loading/page-loading";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";

// The bulk bar's count-chip noun: "1 Item selected".
const ITEM_SELECTION_NOUN = { one: "Item", other: "Items" };

// ---------------------------------------------------------------------------
// Row model
// ---------------------------------------------------------------------------

/**
 * One trash row: a trashed project, the server's soft-deleted issue that
 * core's cron purges once the retention window runs out. The row carries its
 * display fact (`location`) so the columns stay lookup-free.
 */
type TrashPageRow = {
  name: string;
  trashedAt: string | null;
  /** Where the project lives (restore puts it back there): its workflow.
   *  Null renders the `—` empty marker. */
  location: string | null;
  issue: TrashRow;
};

function rowId(row: TrashPageRow): string {
  return row.issue.id;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const h = createColumnHelper<TrashPageRow>();

// Selection leads and the identifying column follows, the projects table
// order.
const TRASH_PINNED_COLUMN_IDS = ["select", "name"];

// Restore and delete are the bulk-action bar's and the row menu's — the
// columns render the entity and its countdown, nothing actionable. The set is
// fixed: what was deleted, where it lived, when it went, who sent it and when
// it goes for good is the whole page, so there is no column settings control
// and nothing persisted per viewer.
const TRASH_COLUMNS: DataTableColumnDef<TrashPageRow>[] = [
  h.display({
    id: "name",
    header: "Name",
    enableSorting: false,
    // The fill column: it takes whatever the fixed columns leave, so the
    // table always reaches the pane's edge and the name gets the room.
    meta: { minWidth: 200, truncate: true, width: 200 },
    cell: ({ row }) => (
      <DataTableCellContent>
        <Text size="sm" weight="medium" truncate={1}>
          {row.original.name}
        </Text>
      </DataTableCellContent>
    ),
  }),
  h.display({
    id: "location",
    header: "Location",
    enableSorting: false,
    meta: { minWidth: 140, truncate: true, width: 180 },
    cell: ({ row }) => (
      <DataTableCellContent>
        <Text size="sm" variant="muted" truncate={1}>
          {row.original.location ?? NONE_OPTION_LABEL}
        </Text>
      </DataTableCellContent>
    ),
  }),
  // Sortable on the ISO string; the cell is the app's "Aug 25" rung —
  // the trash holds 30 days, so the year says nothing, and the full
  // timestamp is the hover.
  h.accessor({
    accessorKey: "trashedAt",
    header: "Deleted On",
    enableSorting: true,
    meta: { minWidth: 100, width: 110 },
    cell: ({ row }) => (
      <DataTableCellContent>
        <DateText date={row.original.trashedAt} format="short" size="sm" />
      </DataTableCellContent>
    ),
  }),
  h.display({
    id: "trashedByUser",
    header: "Deleted By",
    enableSorting: false,
    meta: { minWidth: 90 },
    cell: ({ row }) => {
      const user = row.original.issue.trashedByUser;
      if (!user) {
        return (
          <DataTableCellContent>
            <Text size="sm" variant="muted">Unknown</Text>
          </DataTableCellContent>
        );
      }
      return (
        <DataTableCellContent>
          {/* Avatar-only value cell — read-only, so no cell ring and no
              chip wash, but the chip keeps its hover preview. */}
          <EntityTag
            display="visual"
            entity={{ kind: "user", id: user.userId, user }}
            ghost={false}
          />
        </DataTableCellContent>
      );
    },
  }),
  h.display({
    id: "autoDelete",
    header: "Auto-deletes",
    enableSorting: false,
    meta: { minWidth: 100 },
    cell: ({ row }) => {
      const days = trashDaysRemaining(row.original.trashedAt);
      const label = days > 0 ? `${days}d` : "today";
      // A quiet day count ("20d"); the exact purge moment is the hover
      // tooltip. The count heats up as it runs down: the last week reads
      // in the warning ink, the last day in the destructive ink.
      return (
        <DataTableCellContent>
          <DateText
            date={trashPurgeAt(row.original.trashedAt)}
            fallback={label}
            label={label}
            size="sm"
            variant={
              days <= 1 ? "destructive" : days <= 7 ? "warning" : "muted"
            }
          />
        </DataTableCellContent>
      );
    },
  }),
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function TrashPage({ workspaceKey }: { workspaceKey: string }) {
  const { workflowMap } = useWorkspaceData();
  // The ClickUp split: trash.manage sees the workspace's whole trash and holds
  // the destructive verbs; everyone else gets the self-serve surface — the
  // server scopes the project list to their own deletions, restorable through
  // `restoreIssue` (the same gate their delete passed).
  const canManage = usePermission("trash.manage");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [sorting, setSorting] = useState<SortingState>([
    { id: "trashedAt", desc: true },
  ]);
  const sortDesc = sorting[0]?.desc ?? true;

  const { registerState, getGroupProps } = useGroupStates<TrashPageRow>();

  const { issues, total, initialLoading } = useTrashList(debouncedSearch);

  const { restoreItem, restoreIssue, deleteItemPermanently, clearAllTrash } =
    useTrashMutations();

  // One flat list ordered by deletion time. The rows are already
  // search-narrowed by the query's `where`.
  const rows = useMemo<TrashPageRow[]>(() => {
    const projectRows = issues.map(
      (issue): TrashPageRow => ({
        name: issue.name,
        trashedAt: issue.trashedAt,
        location: workflowMap.get(issue.workflowId)?.name ?? null,
        issue,
      }),
    );
    projectRows.sort((a, b) => {
      const cmp = (a.trashedAt ?? "").localeCompare(b.trashedAt ?? "");
      return sortDesc ? -cmp : cmp;
    });
    return projectRows;
  }, [issues, sortDesc, workflowMap]);

  const localContextKey = `${workspaceKey}:${debouncedSearch}:${sortDesc}`;

  // --- Clear all dialog ---
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const clearAllLabel = debouncedSearch
    ? "Clear filtered items"
    : "Clear all trash";

  const clearAllDescription = debouncedSearch
    ? `Are you sure you want to permanently delete all items matching "${debouncedSearch}"? This action cannot be undone.`
    : `Are you sure you want to permanently delete all ${total.toLocaleString()} item${total !== 1 ? "s" : ""} in trash? This action cannot be undone.`;

  const handleClearAll = useCallback(async () => {
    // A filtered clear purges only what the table shows — the same `where`
    // the listing runs under.
    const where = debouncedSearch ? { search: debouncedSearch } : undefined;
    await clearAllTrash(where);
  }, [clearAllTrash, debouncedSearch]);

  // --- Delete permanently ---
  const [deleteTarget, setDeleteTarget] = useState<TrashPageRow | null>(null);

  const handleDeletePermanently = useCallback((item: TrashPageRow) => {
    setDeleteTarget(item);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteItemPermanently(deleteTarget.issue.id);
    setDeleteTarget(null);
  }, [deleteTarget, deleteItemPermanently]);

  // --- Restore ---
  const handleRestore = useCallback(
    async (item: TrashPageRow) => {
      if (canManage) {
        await restoreItem(item.issue.id);
      } else if (await restoreIssue(item.issue.issueId)) {
        trackEvent({
          context: AnalyticsContext.Settings,
          event: SettingsEvent.Restore,
          label: "trash",
          data: { itemType: "Issue" },
        });
      }
    },
    [canManage, restoreIssue, restoreItem],
  );

  // The bulk bar carries the same two verbs as the row menu. Both mutations
  // are by-id, so a select-all over unloaded rows never arises here (the
  // table has no select-all groups) and each action settles one call per row.
  const bulkActions = useMemo<BulkAction<TrashPageRow>[]>(
    () => [
      {
        id: "restore",
        type: "direct",
        label: "Restore",
        icon: "reload",
        successMessage: (_rows, count) =>
          `${count.toLocaleString()} item${count !== 1 ? "s" : ""} restored`,
        onAction: async (selected) => {
          await settleAll(selected, (item) => handleRestore(item));
        },
      },
      // Permanent deletion is the trash.manage half of the page — a
      // self-serve viewer's only verb is Restore.
      ...(canManage
        ? [
            {
              id: "delete-permanently",
              type: "confirm",
              label: "Delete Permanently",
              icon: "trash",
              destructive: true,
              confirmTitle: (rows) =>
                `Permanently delete ${rows.length.toLocaleString()} item${rows.length !== 1 ? "s" : ""}?`,
              confirmDescription: (rows) =>
                `${rows.length.toLocaleString()} item${rows.length !== 1 ? "s" : ""} will be deleted permanently. This action cannot be undone.`,
              confirmLabel: "Delete Permanently",
              successMessage: (_rows, count) =>
                `${count.toLocaleString()} item${count !== 1 ? "s" : ""} permanently deleted`,
              onAction: async (selected) => {
                await settleAll(selected, (item) =>
                  deleteItemPermanently(item.issue.id),
                );
              },
            } satisfies BulkAction<TrashPageRow>,
          ]
        : []),
    ],
    [canManage, handleRestore, deleteItemPermanently],
  );

  // One ungrouped table — the ALL_GROUP_KEY sentinel is what renders flat
  // (any other key paints a labeled band).
  const groups = useMemo<DataTableGroupType<TrashPageRow>[]>(() => {
    const allProps = getGroupProps(ALL_GROUP_KEY);
    return [
      {
        key: ALL_GROUP_KEY,
        label: "All",
        totalCount: rows.length,
        ...allProps,
        loading: allProps.loading || initialLoading,
      },
    ];
  }, [getGroupProps, initialLoading, rows.length]);

  return (
    <Box direction="column" fill="flex">
      <LocalGroupProvider<TrashPageRow>
        contextKey={localContextKey}
        groupKey={ALL_GROUP_KEY}
        onStateChange={registerState}
        rows={rows}
      />
      <PageHeader>
        <PageHeaderBreadcrumb>
          <BreadcrumbItem>
            {/* The count rides the title as a muted, regular-weight sibling
                on the title's own 14px rung — the stage manager's "· N"
                grammar — so it reads as a fact about the page, not part of
                its name. */}
            <BreadcrumbPage>
              Trash
              {total > 0 && (
                <Text size="sm" variant="muted" weight="normal">
                  {" "}· {total.toLocaleString()}
                </Text>
              )}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </PageHeaderBreadcrumb>
        <PageHeaderActions>
          <ViewSearch
            onValueChange={setDebouncedSearch}
            size="xs"
          />
        </PageHeaderActions>
      </PageHeader>

      {/* Finder's Trash bar: the retention rule and the action it qualifies
          share ONE quiet strip on the table's own gutter — the fact reads as
          part of the list, and Clear all sits beside what it clears. A muted
          well, not a bordered Alert: a standing rule about the page, not a
          message on top of it. The strip is padded, not fixed-height: the
          32px button on the `sm` inset makes 48px, and a wrapped sentence on
          a narrow pane grows it instead of crowding the edges. `md` above
          and below so it stands clear of both the header and the table. */}
      <Box
        direction="column"
        fill="flex"
        gap="md"
        paddingTop="md"
        paddingBottom="lg"
      >
        <Box paddingX="md">
          <Box
            align="center"
            background="tonal"
            direction="row"
            gap="base"
            paddingX="md"
            paddingY="sm"
            rounded="md"
          >
            <Icon color="muted" name="info" size="md" />
            <Box grow>
              <Text size="sm">
                Items in the trash are deleted forever after{" "}
                {TRASH_RETENTION_DAYS} days. Restore anything you still need
                before then.
              </Text>
            </Box>
            {canManage && total > 0 && (
              <Box shrink={false}>
                <Button
                  onClick={() => setClearDialogOpen(true)}
                  shape="square"
                  color="destructive"
                >
                  {debouncedSearch ? "Clear filtered Trash" : "Clear all Trash"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        <DataTable
          groups={groups}
          columns={TRASH_COLUMNS}
          getRowId={rowId}
          bulkActions={bulkActions}
          bulkSelectionNoun={ITEM_SELECTION_NOUN}
          sorting={sorting}
          onSortingChange={setSorting}
          density="regular"
          stickyHeader
          pinnedColumnIds={TRASH_PINNED_COLUMN_IDS}
          columnLayout="fill"
          fillColumnId="name"
          paddingX="md"
          renderRowActions={(item) => (
            <>
              <MenuItem onClick={() => handleRestore(item)}>
                <Icon name="reload" size="md" color="muted" />
                Restore
              </MenuItem>
              {canManage ? (
                <>
                  <MenuSeparator />
                  <MenuItem
                    onClick={() => handleDeletePermanently(item)}
                    variant="destructive"
                  >
                    <Icon name="trash" size="md" />
                    Delete permanently...
                  </MenuItem>
                </>
              ) : null}
            </>
          )}
          emptyState={
            <EmptyState
              className="flex-1"
              description={
                debouncedSearch
                  ? "Try adjusting your search terms"
                  : "Items you delete will appear here for 30 days"
              }
              icon="trash"
              media="icon"
              title={
                debouncedSearch
                  ? "No items match your search"
                  : "Trash is empty"
              }
            />
          }
        />
      </Box>

      {/* Clear all confirmation */}
      <TrashConfirmDialog
        open={clearDialogOpen}
        onOpenChange={setClearDialogOpen}
        title={clearAllLabel}
        description={clearAllDescription}
        confirmLabel={debouncedSearch ? "Clear filtered Trash" : "Clear all Trash"}
        onConfirm={handleClearAll}
      />

      {/* Delete permanently confirmation */}
      <TrashConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Permanently delete item"
        description={
          deleteTarget
            ? `Permanently delete "${deleteTarget.name}" and everything attached to it? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete permanently"
        onConfirm={confirmDelete}
      />
    </Box>
  );
}

/**
 * No permission gate: trash is self-serve. The page waits for the workspace
 * `me` payload itself (the RequirePermission bootstrap rule — on a workspace
 * switch the previous workspace's permissions linger until the refetch), so
 * `canManage` never renders against stale data.
 */
function TrashRoute() {
  const { workspace } = Route.useParams();
  const { currentUser, isLoading } = useAppData();
  if (isLoading || !currentUser) {
    return <PageLoading delayed />;
  }
  return <TrashPage workspaceKey={workspace} />;
}

export const Route = createFileRoute("/(app)/$workspace/settings/trash")({
  component: TrashRoute,
});
